import Button from '@/components/UI/Button';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { Dropdown, DropdownMenuDivider, DropdownMenuItem } from '@/components/UI/Dropdown';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { State } from '@/model/State.model';
import type { Webhook, WebhookEventType } from '@/model/WebhookConfiguration.model';
import { RouteService } from '@/support/RouteService';
import { web as webRoutes } from '@/support/route.ts';
import { successToast } from '@/helpers';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useModalAction } from '@/utils/modal';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { RiMoreFill } from 'react-icons/ri';
import { useSelector } from 'react-redux';

/**
 * Displays a list of webhook configurations for the current tenant,
 * showing the URL, subscribed events, API version, and timestamps for each webhook.
 */
const WebhookConfigurations: FC = () => {
  const { openModal } = useModalAction();
  const settings = useSelector((state: State) => state.ui.settings);

  const { invoke: deleteWebhook } = useApiHook({
    endpoint: 'DELETE /api/v1/webhooks/{webhookId}',
    invokeOnInit: false,
    options: { onError: { toast: true, throw: true, slug: 'delete-webhook' } },
  });

  const { response: eventTypesResponse } = useApiHook({
    endpoint: 'GET /api/v1/webhooks/event-types',
  });

  const eventTypes: Array<WebhookEventType> = useMemo(() => eventTypesResponse?.body ?? [], [eventTypesResponse]);

  const handleDeleteWebhook = useCallback(
    async (webhookId: string, refetchTableData?: () => Promise<void>) => {
      try {
        await deleteWebhook({ path: { webhookId } } as Record<string, unknown>);
        await refetchTableData?.();
        successToast('Webhook deleted');
      } catch {
        // Error already handled by useApiHook (toast + exception capture)
      }
    },
    [deleteWebhook]
  );

  const columns: Array<TanStackColumnDef<Webhook>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        size: 180,
        enableSorting: true,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: Webhook } }) =>
          original ? (
            <Link
              className="link w-100"
              to={webRoutes('team.integrations.webhooks.events', {
                webhookApiId: original.api_id,
              })}
            >
              {original.name}
            </Link>
          ) : null,
      },
      {
        id: 'url',
        header: 'URL',
        accessorKey: 'url',
        size: 280,
        enableSorting: true,
        isVisible: true,
      },
      {
        id: 'events',
        header: 'Events',
        accessorKey: 'events',
        size: 200,
        enableSorting: false,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: Webhook } }) => {
          const text = original.events.join(', ');
          return (
            <span className="db truncate" title={text}>
              {text}
            </span>
          );
        },
      },
      {
        id: 'api_version',
        header: 'API version',
        accessorKey: 'api_version',
        size: 110,
        enableSorting: false,
        isVisible: true,
      },
      {
        id: 'created_at',
        header: 'Created at',
        accessorKey: 'created_at',
        size: 150,
        enableSorting: true,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: Webhook } }) =>
          original ? <DateTimeRenderer value={original.created_at} /> : null,
      },
      {
        id: 'updated_at',
        header: 'Updated at',
        accessorKey: 'updated_at',
        size: 150,
        enableSorting: true,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: Webhook } }) =>
          original ? <DateTimeRenderer value={original.updated_at} /> : null,
      },
      {
        id: 'actions',
        isVisible: true,
        size: 35,
        enableSorting: false,
        cell: ({
          row: { original },
          table,
        }: {
          row: { original: Webhook };
          table: { options: { meta?: { apiTable?: { fetchTableData: () => Promise<void> } } } };
        }) =>
          original ? (
            <Dropdown
              renderMenu={() => (
                <>
                  <DropdownMenuItem
                    onClick={() =>
                      openModal('WEBHOOK_SIGNING_SECRET', {
                        webhook: original,
                        onUpdate: table?.options?.meta?.apiTable?.fetchTableData,
                      })
                    }
                  >
                    Signing Secret
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      openModal('EDIT_WEBHOOK', {
                        webhook: original,
                        onEditWebhook: table?.options?.meta?.apiTable?.fetchTableData,
                        eventTypes,
                      })
                    }
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuDivider />
                  <DropdownMenuItem
                    onClick={() =>
                      openModal('DELETE_WEBHOOK', {
                        onClick: () =>
                          handleDeleteWebhook(original.api_id, table?.options?.meta?.apiTable?.fetchTableData),
                      })
                    }
                  >
                    <span className="red">Delete</span>
                  </DropdownMenuItem>
                </>
              )}
            >
              <span role="button" aria-label="Webhook actions">
                <RiMoreFill size={22} />
              </span>
            </Dropdown>
          ) : null,
      },
    ],
    [openModal, handleDeleteWebhook, eventTypes]
  );

  return (
    <>
      <Header mainHeaderText="Webhooks" />
      <p className="f6 pb2">
        Listen for events from your In Vivo tenant on your webhook endpoints so your integration can automatically
        trigger reactions. Learn more in the{' '}
        <a
          href={RouteService.help('developer_platform')}
          target="_blank"
          rel="noopener noreferrer"
          className="link blue"
          data-testid="api-docs-link"
        >
          API docs
        </a>
        .
      </p>
      <div className="mv3">
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/webhooks',
              path: undefined,
            }).url,
          }}
          columns={columns}
          defaultSortBy={{ id: 'created_at', desc: false }}
          settings={settings}
          reduxTableName="webhookConfigurations"
          hideSearch
          hideFilter
          AsideComponent={({ useTableProps }) => (
            <div className="ml-auto">
              <Button
                testId="add-webhook-button"
                onClick={() =>
                  openModal('ADD_WEBHOOK', {
                    onAddWebhook: useTableProps.apiTable.fetchTableData,
                    eventTypes,
                  })
                }
              >
                Add webhook
              </Button>
            </div>
          )}
        />
      </div>
    </>
  );
};

export default WebhookConfigurations;
